import { useCallback, useEffect, useState } from 'react'
import { Button, Card, CardBody, CardHeader, Input, Switch, useLocale } from '@hk/ui'
import {
  fetchWorkTriggers, formatDate, saveWorkTrigger, translateError,
  type WorkTriggerCode, type WorkTriggerRow,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/* ============================================================================
   **CQ-26 — THE FIVE THINGS THAT RAISE WORK.**  Sprint 83.

   Your answer: *"All five, each independently switchable on or off with its own
   configurable rules."*

   ── EVERY ROW SAYS WHAT STOPS HAPPENING ─────────────────────────────────────────────
   A switch labelled only with a name is a switch nobody dares touch, and a hotel that
   quietly stops raising stayover cleans finds out from a guest. So each row carries the
   sentence describing what the sweep does when it is ON — in the present tense, because
   that is what the hotel is reading about its own mornings.

   ── AND THE DEEP CLEAN PRINTS ITS FIRST DATE ────────────────────────────────────────
   Switching it on for a hotel whose rooms have never had one raises NOTHING on the first
   morning, by design: treating "never" as "overdue" would empty the hotel. Without the
   date beside the switch that is indistinguishable from a switch that does not work, so
   the date is printed — *"rooms with no deep clean yet count from 20 September"* — and it
   becomes a promise rather than a surprise.

   ── PLAIN STATE, NOT REACT-QUERY ────────────────────────────────────────────────────
   The same pattern as `ShiftsCard` beside it, and for the same reason: this card is
   mounted inside `Configuration`, whose own tests render it with no QueryClientProvider.
   ========================================================================= */

export function WorkTriggersCard({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [rows, setRows] = useState<WorkTriggerRow[] | null>(null)
  const [days, setDays] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<string | null>(null)
  const [failed, setFailed] = useState<unknown>(null)

  const load = useCallback(async () => {
    try {
      const got = await fetchWorkTriggers(propertyId)
      setRows(got)
      setDays(Object.fromEntries(got.map(
        (r) => [r.code, r.intervalDays === null ? '' : String(r.intervalDays)])))
    } catch {
      /* **SILENT, AND THAT IS NOT LAZINESS.** This card sits inside a screen that already
         draws its own refusal for somebody without `admin.configuration`; a second one
         underneath would tell the same person the same thing twice. */
      setRows(null)
    }
  }, [propertyId])

  useEffect(() => { void load() }, [load])

  if (rows === null) return null

  const save = async (row: WorkTriggerRow, isActive: boolean) => {
    setBusy(row.code); setFailed(null)
    try {
      const typed = days[row.code]?.trim() ?? ''
      setRows(await saveWorkTrigger(propertyId, row.code, {
        isActive,
        intervalDays: row.needsIntervalDays && typed !== '' ? Number(typed) : null,
      }))
    } catch (failure) {
      setFailed(failure)
      /* **THE SWITCH GOES BACK WHERE IT WAS.** A refused save that left the control
         looking changed would be the screen claiming something the server did not do. */
      await load()
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card>
      <CardHeader title={t.trgTitle} subtitle={t.trgSubtitle} />
      <CardBody className="space-y-3">
        {failed ? (
          <p role="alert" className="text-[12px] text-[#b91c1c] dark:text-[#fca5a5]">
            {String(translateError(failed, locale).message)}
          </p>
        ) : null}

        {rows.map((row) => (
          <div
            key={row.code}
            className="flex flex-wrap items-start justify-between gap-3 border-b
                       border-border/60 pb-3 last:border-0 last:pb-0"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{t.trgNames[row.code]}</p>
              {/* WHAT STOPS HAPPENING. The whole reason a switch is safe to touch. */}
              <p className="text-[12px] text-muted-foreground">{t.trgDoes[row.code]}</p>

              {row.needsIntervalDays && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <label className="text-[12px]" htmlFor={`days-${row.code}`}>
                    {t.trgEvery}
                  </label>
                  <Input
                    id={`days-${row.code}`}
                    className="w-20 tnum"
                    inputMode="numeric"
                    value={days[row.code] ?? ''}
                    onChange={(e) => setDays({ ...days, [row.code]: e.target.value })}
                  />
                  <span className="text-[12px] text-muted-foreground">{t.trgDays}</span>
                  <Button
                    type="button" size="sm" variant="outline"
                    disabled={busy !== null}
                    onClick={() => save(row, row.isActive)}
                  >
                    {t.save}
                  </Button>
                </div>
              )}

              {/* **THE DATE THAT TURNS SILENCE INTO A PROMISE.** */}
              {row.isActive && row.activeFromLocalDate && row.code === 'SCHEDULED_DEEP_CLEAN' && (
                <p className="mt-1 text-[11.5px] text-muted-foreground">
                  {fill(t.trgCountsFrom, {
                    date: formatDate(new Date(row.activeFromLocalDate), locale),
                  })}
                </p>
              )}
            </div>

            <Switch
              checked={row.isActive}
              disabled={busy !== null}
              onCheckedChange={(next) => void save(row, next)}
              aria-label={t.trgNames[row.code]}
            />
          </div>
        ))}
      </CardBody>
    </Card>
  )
}

export const TRIGGER_ORDER: WorkTriggerCode[] = [
  'GUEST_CHECKOUT', 'STAYOVER_SERVICE', 'SCHEDULED_DEEP_CLEAN',
  'VACANT_ROOM_REFRESH', 'ON_REQUEST',
]
